// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import { Button, Card, LgPadding, MdPadding, Tag } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useEffect } from 'react';
import { Platform, View } from 'react-native';
import { selectUser } from '~shared/actions/ui/users';
import { getImage } from '~shared/assets/images/getImage';
import { getImageAltText } from '~shared/assets/images/getImageAltText';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';

type Props = {
  readonly userID: number;
  readonly name?: string;
  readonly paddingBottom?: boolean;
};

export const NeedsInsuranceCard = ({ userID, name, paddingBottom }: Props) => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const currentUserID = useSelectorShared(getCurrentUserID);

  useEffect(() => {
    trackEvent({ event: EVENTS.ADD_INSURANCE_VIEWED });
  }, [trackEvent]);

  const label = 'Out-of-date insurance';
  const messagePerson = (userID !== currentUserID && !!name && `${name}'s `) || 'your ';
  const title = (userID !== currentUserID && !!name ? `${name}'s ` : '') + label;

  const handleUpdateInsurance = () => {
    trackEvent({ event: EVENTS.ADD_INSURANCE_CLICKED });
    dispatch(selectUser(userID));
    navigate('RouteInsuranceSection', { userID });
  };

  // makes sure that styling matches existing homescreen components on web and native
  // NOTE: on web, Container is a plain RN View, which silently ignores topPadding/bottomPadding.
  // paddingBottom currently has no visual effect on web for that reason - if a web call site ever
  // passes paddingBottom={true}, this will need to apply the spacing via style/margin instead.
  const Container = Platform.OS === 'web' ? View : MdPadding;

  return (
    <Container
      topPadding={SPACING.STATIC.NONE}
      bottomPadding={paddingBottom ? SPACING.STATIC.XXL : SPACING.STATIC.NONE}
    >
      <Card
        title={title}
        illustrationSrc={getImage('eIconInsuranceCircleOutOfDate')}
        illustrationAccessibilityLabel={getImageAltText('eIconInsuranceCircleOutOfDate')}
        tag={
          <Tag
            label="ACTION REQUIRED"
            type="warning"
          />
        }
        description={`Get the most out of your prescription benefits by updating ${messagePerson}insurance info.`}
      >
        <LgPadding topPadding={SPACING.STATIC.NONE}>
          <Button
            label="Update now"
            onPress={handleUpdateInsurance}
          />
        </LgPadding>
      </Card>
    </Container>
  );
};
